import { type ReactNode, useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import { compact } from "lodash";
import { twMerge } from "tailwind-merge";
import { useElementSize, useLocalStorage } from "usehooks-ts";

import { useResizeObserver } from "@mantine/hooks";
import {
  type ColumnDef,
  type ExpandedState,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";

import { LoadingIndicator } from "../loading-indicator";
import { Pagination } from "../pagination";

import { MemoizedTableBody, TableBody } from "./table-body";
import { TableHeader } from "./table-header";

export type TableDensity = "compact" | "cozy" | "comfortable";

const TABLE_DENSITY_STORAGE_KEY = "table-density-setting";

export const useTableDensity = () => {
  return useLocalStorage<TableDensity>(
    TABLE_DENSITY_STORAGE_KEY,
    "comfortable",
  );
};

interface TableProps<TData> {
  onRowSelectionChange?: (selectedRowIds: string[]) => void;
  rows: TData[];
  columns: ColumnDef<TData>[];
  getRowId?: (row: TData) => string;
  getSubRows?: (row: TData) => TData[] | undefined;
  rowSelection?: Record<string, boolean>;
  onPaginationChange?: (pagination: {
    pageIndex: number;
    pageSize: number;
  }) => void;
  pagination?: { pageIndex: number; pageSize: number };
  rowCount?: number;
  loading?: boolean;
  className?: string;
  containerClassName?: string;
  sortingState?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  onExpandedChange?: (expanded: ExpandedState) => void;
  multiSort?: boolean;
  pinnedTopRowIds?: string[];
  enableResizing?: boolean;
  enableSubRowSelection?: boolean;
  shouldHighlightOnHover?: boolean;
  columnWidths?: Record<string, number>;
  expanded?: ExpandedState;
  onColumnWidthsChange?: (widths: Record<string, number>) => void;
  noDataContent?: ReactNode;
}

export type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<
  TData,
  TValue
> & {
  sticky?: boolean;
};

const MIN_COLUMN_WIDTH = 40;

export const Table = <TData,>({
  columns,
  enableResizing = true,
  enableSubRowSelection = true,
  rows,
  getRowId,
  getSubRows,
  rowSelection,
  onRowSelectionChange,
  pagination,
  onPaginationChange,
  rowCount,
  className,
  containerClassName,
  sortingState = [],
  onSortingChange,
  onExpandedChange,
  multiSort = true,
  pinnedTopRowIds = [],
  loading = false,
  shouldHighlightOnHover = false,
  columnWidths,
  expanded,
  onColumnWidthsChange,
  noDataContent,
}: TableProps<TData>) => {
  const [density] = useTableDensity();

  const table = useReactTable({
    getRowId,
    getSubRows,
    data: rows,
    columns,
    state: {
      rowSelection,
      pagination,
      sorting: sortingState,
      expanded,
      rowPinning: {
        top: pinnedTopRowIds,
      },
      ...(columnWidths && { columnSizing: columnWidths }),
    },
    isMultiSortEvent: multiSort ? () => true : undefined,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    rowCount,
    enableColumnResizing: enableResizing,
    enableExpanding: true,
    enableRowSelection: true,
    enableSubRowSelection,
    columnResizeMode: "onChange",
    onRowSelectionChange: (updaterOrValue) => {
      if (onRowSelectionChange) {
        const selectedRowIds = Object.keys(
          typeof updaterOrValue === "function"
            ? updaterOrValue(table.getState().rowSelection)
            : updaterOrValue,
        );
        onRowSelectionChange(selectedRowIds);
      }
    },
    onPaginationChange: (updaterOrValue) => {
      if (onPaginationChange) {
        const newPagination =
          typeof updaterOrValue === "function"
            ? updaterOrValue(table.getState().pagination)
            : updaterOrValue;
        onPaginationChange(newPagination);
      }
    },
    onSortingChange: (updaterOrValue) => {
      if (onSortingChange) {
        const newSorting =
          typeof updaterOrValue === "function"
            ? updaterOrValue(table.getState().sorting)
            : updaterOrValue;
        onSortingChange(newSorting);
      }
    },
    onExpandedChange: (updaterOrValue) => {
      if (onExpandedChange) {
        const newExpanded =
          typeof updaterOrValue === "function"
            ? updaterOrValue(table.getState().expanded)
            : updaterOrValue;
        onExpandedChange(newExpanded);
      }
    },
    ...(onColumnWidthsChange && {
      onColumnSizingChange: (updaterOrValue) => {
        if (onColumnWidthsChange) {
          const newSizing =
            typeof updaterOrValue === "function"
              ? updaterOrValue(table.getState().columnSizing)
              : updaterOrValue;

          // Ensure all column widths are at least MIN_COLUMN_WIDTH
          Object.keys(newSizing).forEach((key) => {
            newSizing[key] = Math.max(newSizing[key], MIN_COLUMN_WIDTH);
          });
          onColumnWidthsChange(newSizing);
        }
      },
    }),
  });

  useEffect(() => {
    table.getHeaderGroups().forEach((headerGroup) => {
      headerGroup.headers.forEach((header) => {
        if ((header.column.columnDef as ExtendedColumnDef<TData>)?.sticky) {
          header.column.pin("left");
        }
      });
    });
  }, [table]);

  useEffect(() => {
    if (!enableResizing || !columnWidths) {
      return;
    }

    const headers = table.getFlatHeaders();
    const colSizes: { [key: string]: number } = {};

    for (let i = 0; i < headers.length; i++) {
      const header = headers[i];
      colSizes[header.column.id] = header.column.getSize();
    }

    const currentTableColumnIds = compact(columns.map(({ id }) => id));

    const hasChanges = currentTableColumnIds.some(
      (key) =>
        colSizes[key] !== undefined &&
        columnWidths[key] !== undefined &&
        columnWidths[key] !== colSizes[key],
    );

    if (hasChanges) {
      table.setColumnSizing(columnWidths);
    }
  }, [columnWidths, enableResizing, columns, table]);

  const [tableHeadRef, { height: tableHeadHeight }] =
    useResizeObserver<HTMLTableSectionElement>({
      box: "border-box",
    });

  const columnSizeVars = useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes: { [key: string]: number } = {};
    for (const header of headers) {
      colSizes[`--header-${header.id}-size`] = Math.max(
        header.getSize(),
        MIN_COLUMN_WIDTH,
      );
      colSizes[`--col-${header.column.id}-size`] = Math.max(
        header.column.getSize(),
        MIN_COLUMN_WIDTH,
      );
    }

    return colSizes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.getState().columnSizingInfo, table.getState().columnSizing]);

  const [currentColumnIndexHovered, setCurrentColumnIndexHovered] = useState<
    number | null
  >(null);

  const [containerRef, containerSize] = useElementSize();

  return (
    <div className={twMerge("relative h-full", containerClassName)}>
      {loading && (
        <div
          className={clsx(
            "absolute left-[1px] right-[1px] top-[1px] flex items-center justify-center",
            "z-[9] bg-v2-glass-inverse rounded-lg",
          )}
          style={{ height: containerSize.height - 2 }}
        >
          <LoadingIndicator />
        </div>
      )}
      {!loading && rows.length === 0 && (
        <div
          className={clsx(
            "absolute left-[1px] right-[1px] top-[1px] flex items-center justify-center",
            "z-[9] rounded-lg",
          )}
          style={{ height: containerSize.height - 2 }}
        >
          <div className="mt-8">{noDataContent || "No data"}</div>
        </div>
      )}
      <div
        ref={containerRef}
        className={twMerge(
          clsx(
            "border border-v2-secondary bg-v2-primary rounded-lg relative w-full overflow-auto",
            {
              "min-h-[256px] overflow-hidden": loading || rows.length === 0,
            },
            className,
          ),
        )}
      >
        <table
          className={clsx(
            `table-fixed border-separate border-spacing-0 table-density-${density}`,
            {
              "w-full": !enableResizing,
            },
          )}
          style={{ ...columnSizeVars }}
        >
          <thead ref={tableHeadRef as React.LegacyRef<HTMLTableSectionElement>}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <TableHeader
                    key={header.id}
                    header={header}
                    index={index}
                    headerGroupLength={headerGroup.headers.length}
                  />
                ))}
              </tr>
            ))}
          </thead>
          {table.getState().columnSizingInfo.isResizingColumn ? (
            <MemoizedTableBody
              columns={columns}
              table={table}
              tableHeadHeight={tableHeadHeight}
              loading={loading}
            />
          ) : (
            <TableBody
              columns={columns}
              table={table}
              tableHeadHeight={tableHeadHeight}
              loading={loading}
              shouldHighlightOnHover={shouldHighlightOnHover}
              currentColumnIndexHovered={currentColumnIndexHovered}
              setCurrentColumnIndexHovered={setCurrentColumnIndexHovered}
            />
          )}
        </table>
      </div>
      {pagination && (
        <div className="my-4 text-v2-primary">
          <Pagination
            pageSize={table.getState().pagination.pageSize}
            currentPage={table.getState().pagination.pageIndex}
            rowCount={rowCount}
            onChangePageSize={table.setPageSize}
            onChangePage={table.setPageIndex}
          />
        </div>
      )}
    </div>
  );
};
